/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, ReactElement, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import DisplayAmount from "../../shared/DisplayAmount";
import axios from "axios";
import { Tooltip, IconButton } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { MdOutlineCancel } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { MdRemoveCircleOutline } from "react-icons/md";
import { ProfileContext } from "../../../../store/ProfileProvider";
import SweetAlert2 from "react-sweetalert2";
import RequestWhitelistAddressApproveConfirmModal from "../../shared/RequestWhitelistAddressApproveConfirmModal";
import RequestWhitelistAddressRejectConfirmModal from "../../shared/RequestWhitelistAddressRejectConfirmModal";
import RequestWhitelistAddressBlacklistConfirmModal from "../../shared/RequestWhitelistAddressBlacklistConfirmModal";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  wlad_id: string;
  status: string;
  address: string;
  asset: string;
  created_at: string;
  reason?: string;
  merchant: Merchant;
  subaccount: Subaccount;
};

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const AccountBalancesTab: React.FC<{}> = () => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [swalProps, setSwalProps] = useState<any>({});
  const { subaccount_id } = useParams();

  const [whitelistAddressToApprove, setWhitelistAddressToApprove] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressApproveConfirmModal,
    setIsOpenWhitelistAddressApproveConfirmModal,
  ] = useState<boolean>(false);

  // Reject modal states
  const [whitelistAddressToReject, setWhitelistAddressToReject] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressRejectConfirmModal,
    setIsOpenWhitelistAddressRejectConfirmModal,
  ] = useState<boolean>(false);

  // Blacklist modal states
  const [whitelistAddressToBlacklist, setWhitelistAddressToBlacklist] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressBlacklistConfirmModal,
    setIsOpenWhitelistAddressBlacklistConfirmModal,
  ] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await fetchWhitelistAddresses({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
      });
      setIsLoading(false);
    };

    fetchData();
  }, []); // eslint-disable-line

  const fetchWhitelistAddresses = async (payload: PayloadType = {}) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        subaccount_id: subaccount_id,
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/trade/whitelist-addresses?${queryString}`
      );

      let newData = response.data.whitelist_addresses?.payload;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      setIsLoading(false);

      // Manage Last Key and Total Record
      let newLastKey = response.data.whitelist_addresses?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error) {
      setTableData([]);
      setIsLoading(false);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      subaccount_id: subaccount_id,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        subaccount_id: subaccount_id,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    // setFilters(newFilters);
    fetchWhitelistAddresses(newFilters);
    // }
  };

  interface WhitelistAddressestatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: WhitelistAddressestatusItems[] = [
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "approved",
      value: "approved",
      display: (
        <Badge className={`${statusClasses} status-approved`}>Approved</Badge>
      ),
    },
    {
      status: "rejected",
      value: "rejected",
      display: (
        <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "blacklist",
      value: "blacklist",
      display: (
        <Badge className={`${statusClasses} status-blacklist`}>Blacklist</Badge>
      ),
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      flex: 1,
      minWidth: 180,
      maxWidth: 180,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        formatDate(params.row.created_at),
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      sortable: false,
      minWidth: 160,
      maxWidth: 160,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.subaccount.name,
    },
    {
      field: "nickname",
      headerName: "Nickname",
      sortable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 180,
      maxWidth: 180,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 80,
      maxWidth: 80,
    },
    {
      field: "network",
      headerName: "Network",
      sortable: false,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 140,
      maxWidth: 140,
    },
    {
      field: "address",
      headerName: "Address",
      sortable: false,
      flex: 1,
      minWidth: 500,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        params.row.address,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => {
        const status = params.value as string;
        const statusItem = statusList.find((item) => item.status === status);
        return (
          <>
            {(status === "rejected" || status === "blacklist") &&
            params.row.reason ? (
              <Tooltip title={params.row.reason} arrow placement="top">
                <div className="cursor-help">
                  {statusItem ? statusItem.display : null}
                </div>
              </Tooltip>
            ) : (
              <>{statusItem ? statusItem.display : null}</>
            )}
          </>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      headerAlign: "right",
      align: "right",
      flex: 1,
      minWidth: 55,
      renderCell: (params) => {
        const { status } = params?.row || {};
        const canApprove = status === "pending";
        const canReject = status === "pending";
        const canBlacklist = status === "approved";
        return (
          <>
            {canApprove && (
              <Tooltip title="Mark as Approve" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-approve-${params?.row?.wlad_id}`}
                    onClick={() => handleClickApprove(params.row.wlad_id)}
                  >
                    <AiOutlineCheckCircle className="status-approved h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {canReject && (
              <Tooltip title="Mark as Reject" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-reject-${params?.row?.wlad_id}`}
                    onClick={() => handleClickReject(params.row.wlad_id)}
                  >
                    <MdOutlineCancel className="status-rejected h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {canBlacklist && (
              <Tooltip title="Mark as Blacklist" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-blacklist-${params?.row?.wlad_id}`}
                    onClick={() => handleClickBlacklist(params.row.wlad_id)}
                  >
                    <MdRemoveCircleOutline className="status-rejected h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const formatAddress = (address?: string): string => {
    if (!address || address.length < 24) return "N/A";
    return `${address.slice(0, 15)}...${address.slice(-9)}`;
  };

  // Approve handlers
  const handleClickApprove = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToApprove(whitelistAddress);
    showWhitelistAddressApproveConfirmModal();
  };

  const showWhitelistAddressApproveConfirmModal = () => {
    setIsOpenWhitelistAddressApproveConfirmModal(true);
  };

  const closeWhitelistAddressApproveConfirmModal = () => {
    setWhitelistAddressToApprove(null);
    setIsOpenWhitelistAddressApproveConfirmModal(false);
  };

  // Reject handlers
  const handleClickReject = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToReject(whitelistAddress);
    showWhitelistAddressRejectConfirmModal();
  };

  const showWhitelistAddressRejectConfirmModal = () => {
    setIsOpenWhitelistAddressRejectConfirmModal(true);
  };

  const closeWhitelistAddressRejectConfirmModal = () => {
    setWhitelistAddressToReject(null);
    setIsOpenWhitelistAddressRejectConfirmModal(false);
  };

  // Blacklist handlers
  const handleClickBlacklist = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToBlacklist(whitelistAddress);
    showWhitelistAddressBlacklistConfirmModal();
  };

  const showWhitelistAddressBlacklistConfirmModal = () => {
    setIsOpenWhitelistAddressBlacklistConfirmModal(true);
  };

  const closeWhitelistAddressBlacklistConfirmModal = () => {
    setWhitelistAddressToBlacklist(null);
    setIsOpenWhitelistAddressBlacklistConfirmModal(false);
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const triggerReloadList = async () => {
    setIsLoading(true);
    await fetchWhitelistAddresses({
      page: page,
      limit: rowPerPage,
      lastkey: page !== 0 ? currentLastKey : "",
    });
  };

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start text-md",
    row: "",
  };

  return (
    <>
      {/* Approve Modal */}
      {isOpenWhitelistAddressApproveConfirmModal &&
        whitelistAddressToApprove && (
          <RequestWhitelistAddressApproveConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenWhitelistAddressApproveConfirmModal}
            dataToApprove={whitelistAddressToApprove}
            onClose={closeWhitelistAddressApproveConfirmModal}
          />
        )}

      {/* Reject Modal */}
      {isOpenWhitelistAddressRejectConfirmModal && whitelistAddressToReject && (
        <RequestWhitelistAddressRejectConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressRejectConfirmModal}
          dataToReject={whitelistAddressToReject}
          onClose={closeWhitelistAddressRejectConfirmModal}
        />
      )}

      {/* Blacklist Modal */}
      {isOpenWhitelistAddressBlacklistConfirmModal &&
        whitelistAddressToBlacklist && (
          <RequestWhitelistAddressBlacklistConfirmModal
            setSwalProps={setSwalProps}
            triggerReload={triggerReloadList}
            isOpen={isOpenWhitelistAddressBlacklistConfirmModal}
            dataToBlacklist={whitelistAddressToBlacklist}
            onClose={closeWhitelistAddressBlacklistConfirmModal}
          />
        )}
      <div className="px-4 py-8">
        <Box>
          <DataGrid
            sx={{
              "& .MuiDataGrid-cell": {
                padding: "8px 16px",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: "8px 16px",
              },
              "& .MuiTablePagination-input": {
                marginRight: "-20px",
              },
              "& .MuiTablePagination-displayedRows": {
                display: "none",
              },
            }}
            loading={isLoading}
            rows={tableData || []}
            columns={columns}
            getRowId={(row) => row.wlad_id}
            rowCount={rowCount}
            sortingMode="server"
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            rowHeight={48}
            columnHeaderHeight={48}
            paginationModel={{ page: page, pageSize: pageSize }}
            onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            classes={customDataGridStyles}
            hideFooter={currentLastKey === "" && page === 0}
            autoHeight
            localeText={{
              noRowsLabel: "No data available",
            }}
          />
        </Box>

        <SweetAlert2
          {...swalProps}
          customClass={{
            actions: "custom-swal2-popup-action",
            title: "custom-swal2-popup-title",
            confirmButton:
              "custom-swal2-popup-action custom-swal2-popup-action-confirm",
            cancelButton:
              "custom-swal2-popup-action custom-swal2-popup-action-cancel",
          }}
        ></SweetAlert2>
      </div>
    </>
  );
};

export default AccountBalancesTab;
